import { HostBinding, Component, OnInit, ElementRef, ViewChild, Injector } from '@angular/core';
import * as moment from 'moment';
import * as bowser from 'bowser';
import { MAT_DATE_FORMATS, MatDatepicker } from '@angular/material';

import { EventsComponent } from '../events/events.component';

const DATE_FORMAT = 'MM/DD/YYYY';
const MATERIAL_DATEPICKER_FORMATS = {
	display: {
		dateInput: 'DD/MM/YYYY',
		monthYearLabel: 'MMMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};

@Component({
	selector: 'Datepicker-accessibility',
	template: `
		<div class="datepicker-wrapper">
			<div class="datepicker" [ngClass]="{ disabled: control.disabled, error: error }">
				<div class="input-container">
					<form #maskedInput [ngClass]="{ error: error, disabled: control.disabled }" class="datepicker-form">
						<!-- datepicker input - the hidden input -->
						<input
							class="hideMe"
							type="tel"
							[min]="minDate"
							[max]="maxDate"
							(dateChange)="updateDatepickerDate($event)"
							matInput
							[value]="datepickerValue"
							[matDatepicker]="picker"
							[attr.gtm]="gaLabel"
						/>
						<!-- Masked input - the visible input -->
						<input
							class="masked-datepicker"
							[attr.id]="control.name.split('PolicyData.').join('')"
							[attr.aria-describedby]="control.name.split('.').join('_') + '_error'"
							[ngClass]="{ textcolor: value && value.length > 0, 'is-datepicker-opened': picker.opened }"
							name="maskedInput"
							mask="39/19/9999"
							placeholder="MM/DD/YYYY"
							[disabled]="control.disabled"
							autocomplete="off"
							[attr.pattern]="inputPattern"
							[attr.inputmode]="inputMode"
							[textMask]="mask"
							[(ngModel)]="value"
							(keydown.enter)="toggleDatepicker()"
							(focus)="onDatepickerFocus()"
							(blur)="updateInputBlur($event.target.value)"
						/>
					</form>
					<mat-datepicker
						(opened)="onDatepickerOpened()"
						(closed)="onDatepickerClosed()"
						[startAt]="datepickerValue"
						#picker
					>
					</mat-datepicker>
				</div>
			</div>
			<button
				[ngClass]="{ disabled: this.control.disabled }"
				[disabled]="this.control.disabled"
				class="icon new"
				tabindex="0"
				(keydown)="keydownDatepicker($event)"
				(click)="toggleDatepicker()"
				aria-Label="Open Date Picker Dialog"
			></button>
		</div>
	`,
	providers: [
		{
			provide: MAT_DATE_FORMATS,
			useValue: MATERIAL_DATEPICKER_FORMATS,
		},
	],
	styleUrls: ['./datepicker-accessibility.component.scss'],
})
export class DatepickerAccessibilityComponent extends EventsComponent implements OnInit {
	@ViewChild('maskedInput', { static: true, read: ElementRef }) maskedInput;
	@ViewChild('picker', { static: true, read: MatDatepicker }) picker: MatDatepicker<Date>;

	datepickerValue: Date;
	minDate;
	maxDate;
	keydownListenerFn;
	currentDateFormatted: string;
	inputPattern: string;
	inputMode: string;
	@HostBinding('attr.name')
	name = this.control ? this.control.name : '';
	public mask = {
		mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
	};

	ngOnInit() {
		this.gaLabel = this.getGaLabel(this.control);
		if (this.value) {
			this.reportPrefill();
		}
		this.currentDateFormatted = this.getFormattedDateString(this.value);
		this.datepickerValue = this.value ? new Date(this.value) : null;
		this.isMobile =
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'mobile' ||
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'tablet';

		if (this.control.attributes) {
			if (this.control.attributes.maxDate && this.control.attributes.minDate) {
				this.maxDate = this.handleDates(this.control.attributes.maxDate);
				this.minDate = this.handleDates(this.control.attributes.minDate);
			}

			if (this.isMobile && this.control.attributes.DynamicMobileKeyboard) {
				this.inputPattern = '\\d*';
				this.inputMode = 'numeric';
			}
		}
	}

	updateDatepickerDate(event) {
		this.datepickerValue = new Date(event.value);
		this.updateDate(event.value);
	}

	updateInputBlur(value) {
		this.datepickerValue = new Date(value);
		this.updateDate(value);
	}

	updateDate(date: string | Date | moment.Moment) {
		const newDateFormatted = this.getFormattedDateString(date);

		this.value = newDateFormatted;

		if (newDateFormatted !== this.currentDateFormatted) {
			return this.setAnswer(newDateFormatted);
		}
		this.currentDateFormatted = newDateFormatted;
	}

	getFormattedDateString(date: string | Date | moment.Moment): string {
		const formattedDateString = moment(date, DATE_FORMAT);

		if (formattedDateString.isValid()) {
			return formattedDateString.format(DATE_FORMAT);
		} else {
			return null;
		}
	}

	handleDates(shiftDateBy: string): Date {
		const amount = shiftDateBy.slice(0, -1);
		const unit = shiftDateBy[shiftDateBy.length - 1] === 'm' ? 'M' : shiftDateBy[shiftDateBy.length - 1]; // moment expects capital M for month unit
		return moment()
			.add(amount, unit as moment.unitOfTime.DurationConstructor)
			.toDate();
	}

	keydownDatepicker(event) {
		if (event.keyCode == 32 || event.keyCode == 13) {
			this.toggleDatepicker();
		}
	}

	toggleDatepicker() {
		if (!this.control.disabled) {
			if (this.picker.opened) {
				this.picker.close();
			} else {
				this.picker.open();
			}
		}
	}

	onDatepickerFocus() {
		if (!this.picker.opened) {
			const label = this.control.title;
			this.splunkAnalyticsService.publish('TextBoxFocus', label || '', '');
		}
	}

	onDatepickerOpened() {
		this.keydownListenerFn = this.Renderer.listen('document', 'keydown', this.datepickerOpenedKeydownHandler);
	}

	onDatepickerClosed() {
		this.keydownListenerFn();
	}

	datepickerOpenedKeydownHandler(event: KeyboardEvent) {
		if (event.keyCode === 8) {
			// prevent IE from navigating to the previous page when Backspace is pressed, if the datepicker is opened.
			event.preventDefault();
		}
	}
}
